import axios from 'axios';

// Use relative URL for production (same domain) or env variable for development
const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? process.env.REACT_APP_BACKEND_URL + '/api'
  : '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add interceptor to include session token from localStorage if cookie fails
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log error details for debugging
    if (error.response) {
      console.error('API Error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('Network Error: No response received', error.message);
    } else {
      console.error('Request Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Auth
export const createSession = (sessionId) => 
  api.post('/auth/session', {}, {
    headers: { 'X-Session-ID': sessionId }
  });

export const getMe = () => api.get('/auth/me');
export const logout = () => {
  localStorage.removeItem('session_token');
  localStorage.removeItem('user');
  return api.post('/auth/logout');
};

// Email/Password Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const verifyEmail = (token) => api.post('/auth/verify-email', null, { params: { token } });
export const resendVerification = (email) => api.post('/auth/resend-verification', null, { params: { email } });
export const forgotPassword = (email) => api.post('/auth/forgot-password', null, { params: { email } });
export const resetPassword = (token, newPassword) => api.post('/auth/reset-password', null, { params: { token, new_password: newPassword } });
export const refreshToken = () => api.post('/auth/refresh');

// Products
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// Cart
export const getCart = () => api.get('/cart');
export const addToCart = (data) => api.post('/cart', data);
export const updateCartItem = (id, data) => api.put(`/cart/${id}`, data);
export const removeFromCart = (id) => api.delete(`/cart/${id}`);
export const clearCart = () => api.delete('/cart');

// Orders
export const createOrder = (data) => api.post('/orders', data);
export const getOrders = () => api.get('/orders');
export const getOrder = (id) => api.get(`/orders/${id}`);
export const updateOrderStatus = (id, status, trackingNumber) => 
  api.put(`/orders/${id}/status`, { status, tracking_number: trackingNumber });

// Payments
export const createStripeCheckout = (orderId) => 
  api.post('/payments/stripe/create-checkout', null, { params: { order_id: orderId } });
export const getStripePaymentStatus = (sessionId) => 
  api.get(`/payments/stripe/status/${sessionId}`);

// Blog
export const getBlogPosts = () => api.get('/blog');
export const getBlogPost = (id) => api.get(`/blog/${id}`);
export const createBlogPost = (data) => api.post('/blog', data);

// Analytics
export const getAnalytics = () => api.get('/analytics/dashboard');

export default api;